import type { IsoDate, IsoDateTime } from './common';
import type { LocationType } from './warehouse';

// Cycle counts (göz sayımı). A bin is counted blind: the counter sees what to count, never
// how many the system expects. A count that matches unlocks the bin; a difference waits for
// a supervisor (sayım farkı / fire onayı), whose approval corrects the stock.

export const COUNT_TASK_STATUSES = ['open', 'review', 'closed', 'cancelled'] as const;
export type CountTaskStatus = (typeof COUNT_TASK_STATUSES)[number];

export const COUNT_TASK_STATUS_LABELS: Readonly<Record<CountTaskStatus, string>> = {
  open: 'Sayılacak',
  review: 'Onay bekliyor',
  closed: 'Kapandı',
  cancelled: 'İptal edildi',
};

/** short_pick: the picker found less than reserved; scheduled: a supervisor's count order */
export const COUNT_REASONS = ['short_pick', 'scheduled'] as const;
export type CountReason = (typeof COUNT_REASONS)[number];

export const COUNT_REASON_LABELS: Readonly<Record<CountReason, string>> = {
  short_pick: 'Eksik toplama',
  scheduled: 'Periyodik sayım',
};

export type CountResolution = 'matched' | 'adjusted' | 'cancelled';

export const COUNT_RESOLUTION_LABELS: Readonly<Record<CountResolution, string>> = {
  matched: 'Sayım tuttu',
  adjusted: 'Fark onaylandı, stok düzeltildi',
  cancelled: 'İptal edildi',
};

export interface CountTaskSummary {
  readonly id: string;
  readonly taskNumber: string;
  readonly status: CountTaskStatus;
  readonly reason: CountReason;
  readonly note: string | null;
  readonly round: number;
  readonly resolution: CountResolution | null;
  readonly locationId: string;
  readonly locationCode: string;
  readonly locationBarcode: string;
  readonly locationType: LocationType;
  readonly createdAt: IsoDateTime;
  readonly countedAt: IsoDateTime | null;
  readonly closedAt: IsoDateTime | null;
  /** lines of the last count that differ (for the supervisor's queue; null before a count) */
  readonly differences: number | null;
}

/** A product to count in the bin: no quantity, ever. */
export interface CountSheetLine {
  readonly tenantId: string;
  readonly tenantCode: string;
  readonly productId: string;
  readonly sku: string;
  readonly name: string;
  readonly imageUrl: string | null;
  /** scanning one adds `units` (a case barcode counts the whole case) */
  readonly barcodes: readonly { readonly code: string; readonly units: number }[];
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly trackLot: boolean;
  readonly trackExpiry: boolean;
}

export interface CountSheet {
  readonly task: CountTaskSummary;
  readonly lines: readonly CountSheetLine[];
}

export interface CountedLine {
  readonly productId: string;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly countedQty: number;
}

export interface CountSubmission {
  /** the bin label scanned at the shelf */
  readonly locationBarcode: string;
  readonly lines: readonly CountedLine[];
}

export interface CountSubmitResult {
  readonly taskId: string;
  readonly taskNumber: string;
  readonly locationCode: string;
  /** closed: the count matched and the bin is unlocked; review: a supervisor decides */
  readonly status: 'closed' | 'review';
}

export interface CountReviewLine {
  readonly tenantCode: string;
  readonly sku: string;
  readonly name: string;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly expected: number;
  readonly counted: number;
  readonly difference: number;
}

/** The supervisor's view of a count: every round, expected next to counted. */
export interface CountReview {
  readonly task: CountTaskSummary;
  readonly rounds: readonly {
    readonly round: number;
    readonly countedBy: string | null;
    readonly countedAt: IsoDateTime | null;
    readonly lines: readonly CountReviewLine[];
  }[];
}

export interface CountApproval {
  readonly taskNumber: string;
  readonly locationCode: string;
  readonly adjustments: readonly { readonly sku: string; readonly lotNo: string | null; readonly difference: number }[];
  /** orders whose reservation in the bin no longer fit: reserved again elsewhere or waiting for stock */
  readonly requeuedOrders: readonly { readonly orderNumber: string; readonly status: 'allocated' | 'backordered' | 'on_hold' }[];
}

export interface CountOrderRequest {
  readonly warehouseId: string;
  /** bins by code; or a block of an aisle */
  readonly codes?: readonly string[];
  readonly aisle?: string;
  readonly rackFrom?: number;
  readonly rackTo?: number;
  /** only bins that hold stock */
  readonly onlyWithStock?: boolean;
  readonly note?: string | null;
}

export interface CountOrderResult {
  readonly created: number;
  /** bins that already had an open count */
  readonly skipped: number;
  readonly tasks: readonly CountTaskSummary[];
}

/** What identifies a line of a count: one product, lot and expiry in the bin. */
export interface CountKeyed {
  readonly productId: string;
  readonly lotNo: string | null;
  readonly expiryDate: string | null;
}

export const countKey = (l: CountKeyed) => `${l.productId}|${l.lotNo ?? ''}|${l.expiryDate ?? ''}`;

export interface CountComparison<K extends CountKeyed> {
  readonly lines: readonly { readonly item: K; readonly expected: number; readonly counted: number; readonly difference: number }[];
  /** every line counted exactly what was expected */
  readonly matched: boolean;
}

/**
 * Counted against expected per key: an expected key nobody reported counts as zero, a key only
 * the counter has was found unexpectedly. The same quantity everywhere means the count matches.
 */
export function compareCount<K extends CountKeyed>(expected: readonly (K & { readonly qty: number })[], counted: readonly (K & { readonly qty: number })[]): CountComparison<K> {
  const lines = new Map<string, { item: K; expected: number; counted: number }>();
  const add = (line: K & { readonly qty: number }, side: 'expected' | 'counted') => {
    const key = countKey(line);
    const known = lines.get(key) ?? { item: line, expected: 0, counted: 0 };
    known[side] += line.qty;
    lines.set(key, known);
  };
  for (const e of expected) add(e, 'expected');
  for (const c of counted) add(c, 'counted');
  const all = [...lines.values()].map((l) => ({ ...l, difference: l.counted - l.expected }));
  return { lines: all, matched: all.every((l) => l.difference === 0) };
}
